import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import { normalizeSpecificDays, normalizeWeeklySchedule } from '@/utils/availability';

export type AgendaConfiguration = {
  id: number;
  primaryColor: string;
  name: string;
  userId: string;
  weeklySchedule: WeeklySchedule;
  specificDays?: DayWithHours[];
  busyDays?: DayWithHours[];
  sessionDuration: number;
  dateRangeOption: number;
  dateRangeDays: number;
  dateRangeStartDate: string;
  dateRangeEndDate: string;
  timeBeforeSessions: number;
  timeAfterSessions: number;
  startTimeIncrements: number;
  minimumNotice: number;
  paymentAmount: number;
  paymentCurrency: string;
  paymentOption: number;
  paymentPolicy: string | null;
  bookingUrl: string;
  showPaymentLink: boolean;
  showPrice: boolean;
  isOnSiteAllowed: boolean;
  isVirtualAllowed: boolean;
  locationDetails: string | null;
  isPatientPaymentReminderActive: boolean;
  isBookingPaymentReminderActive: boolean;
  details?: string;
  isCanceled: boolean;
  overwriteAvailability: boolean;
  overwriteLocation: boolean;
};

export type AgendaConfigurationCreate = {
  name: string;
  details: string;
  primaryColor: string;
}

export type Availability = {
  weeklySchedule: WeeklySchedule;
  specificDays?: DayWithHours[];
  busyDays?: DayWithHours[];
}

export type LocationOptions = {
  isOnSiteAllowed: boolean;
  isVirtualAllowed: boolean;
  locationDetails: string;
}

export type Hour = {
  start: string;
  end: string;
};

export type DayWithHours = {
  date: string;
  hours: Hour[];
};

export type WeeklySchedule = {
  monday: Hour[];
  tuesday: Hour[];
  wednesday: Hour[];
  thursday: Hour[];
  friday: Hour[];
  saturday: Hour[];
  sunday: Hour[];
};

export type IndexListResponse = {
  id: number, 
  primaryColor: string,
  name: string,
  details?: string,
  isCanceled: boolean,
  sessionDuration: number,
  paymentAmount: number,
  paymentCurrency: string,
  showPrice: boolean,
  isOnSiteAllowed: boolean,
  isVirtualAllowed: boolean,
  overwriteAvailability: boolean,
  overwriteLocation: boolean
}

export type AgendaConfigurationIndexResponse = {
  first: AgendaConfiguration;
  list: IndexListResponse[]
}

export type AgendaConfigurationCreateResponse = {
  new: AgendaConfiguration;
  list: IndexListResponse[]
}

export type AgLinkResponse = {
  id: number, name: string, bookingUrl: string
}

export type AllLinksResponse = {
  userUrl: string;
  agendaUrls: AgLinkResponse[]
}

export interface AgendaConfigurationMixin {
  clientTimezone?: string | undefined;
  fetch: (path: string, init?: RequestInit) => Promise<any>;
  getAgendaConfigurations(
    userId?: string | null,
    extension?: string | null
  ): Promise<AgendaConfigurationIndexResponse>
  getAgendaConfiguration: (
    id: number, extension?: string
  ) => Promise<AgendaConfiguration>;
  getAgendaConfigurationByUrl: (
    userUrl: string, agendaUrl: string
  ) => Promise<AgendaConfiguration>;
  getAllLinks(): Promise<AllLinksResponse>;
  createAgendaConfiguration(name: string, details: string): Promise<AgendaConfiguration>;
  updateAgendaConfiguration: (
    agendaConfiguration: Partial<AgendaConfiguration> & { id: number }
  ) => Promise<AgendaConfiguration>;
  deleteAgendaConfiguration(id: number): Promise<{message: string}>
}
export const AgendaConfigurationMixin = {
  async fetch(path: string, init?: RequestInit): Promise<any> {
    throw new Error('Not implemented');
  },
  async getAgendaConfigurations(
    userId: string | null,
    extension: string | null = null
  ): Promise<AgendaConfigurationIndexResponse> {
    const res: AgendaConfigurationIndexResponse = await this.fetch(
      `/agenda_configurations${userId ? `?user_id=${userId}` : extension ? `?extension=${extension}` : ''}`,
      { method: 'get' }
    );
    const agendaConfiguration = res.first;
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;

    return {
      first: {
        ...agendaConfiguration,
        weeklySchedule: normalizeWeeklySchedule(agendaConfiguration.weeklySchedule, 'UTC', timeZone),
        specificDays: normalizeSpecificDays(agendaConfiguration.specificDays || [], 'UTC', timeZone),
        busyDays: normalizeSpecificDays(agendaConfiguration.busyDays || [], 'UTC', timeZone),
      },
      list: res.list
    };
  },
  async getAgendaConfiguration(
    id: number, extension?: string
  ): Promise<AgendaConfiguration> {
    const agendaConfiguration = await this.fetch(
      `/agenda_configurations/${id}${extension ? `?extension=${extension}` : ""}`,
      { method: 'get' }
    );
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;

    return {
      ...agendaConfiguration,
      weeklySchedule: normalizeWeeklySchedule(agendaConfiguration.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(agendaConfiguration.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(agendaConfiguration.busyDays || [], 'UTC', timeZone),
    };
  },
  async getAgendaConfigurationByUrl(
    userUrl: string, agendaUrl: string
  ): Promise<AgendaConfiguration> {
    const agendaConfiguration = await this.fetch(
      `/agenda_configurations/get_by_url?extension=${userUrl}&agenda_url=${agendaUrl}`,
      { method: 'get' }
    );
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;

    return {
      ...agendaConfiguration,
      weeklySchedule: normalizeWeeklySchedule(agendaConfiguration.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(agendaConfiguration.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(agendaConfiguration.busyDays || [], 'UTC', timeZone),
    };
  },
  async getAllLinks(): Promise<AllLinksResponse> {
    const res = this.fetch("/agenda_configurations/get_all_links");
    return res;
  },
  async createAgendaConfiguration(name: string, details: string): Promise<AgendaConfiguration> {
    const agendaConfiguration = await this.fetch(`/agenda_configurations`, {
      method: "post",
      body: JSON.stringify({
        name, details
      })
    });
    const timeZone = this.clientTimezone
      ? this.clientTimezone
      : DateTime.local().zoneName;
    const resObj = {
      ...agendaConfiguration,
      weeklySchedule: normalizeWeeklySchedule(agendaConfiguration.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(agendaConfiguration.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(agendaConfiguration.busyDays || [], 'UTC', timeZone),
    };

    return resObj;
  },
  async updateAgendaConfiguration(
    agendaConfiguration: Partial<AgendaConfiguration> & { id: number }
  ): Promise<AgendaConfiguration> {
    const timeZone = this.clientTimezone ? this.clientTimezone : DateTime.local().zoneName;
    const body = cloneDeep({ ...agendaConfiguration });

    if (agendaConfiguration.weeklySchedule) {
      body.weeklySchedule = normalizeWeeklySchedule(
        cloneDeep(agendaConfiguration.weeklySchedule),
        timeZone,
        'UTC'
      );
    }

    if (agendaConfiguration.specificDays) {
      body.specificDays = normalizeSpecificDays(
        cloneDeep(agendaConfiguration.specificDays),
        timeZone,
        'UTC'
      );
    }

    if (agendaConfiguration.busyDays) {
      body.busyDays = normalizeSpecificDays(
        cloneDeep(agendaConfiguration.busyDays),
        timeZone,
        'UTC'
      );
    }
    const agendaConf = await this.fetch(`/agenda_configurations/${agendaConfiguration.id}`, {
      method: 'put',
      body: JSON.stringify({ ...body }),
    });
    return {
      ...agendaConf,
      weeklySchedule: normalizeWeeklySchedule(agendaConf.weeklySchedule, 'UTC', timeZone),
      specificDays: normalizeSpecificDays(agendaConf.specificDays || [], 'UTC', timeZone),
      busyDays: normalizeSpecificDays(agendaConf.busyDays || [], 'UTC', timeZone),
    };
  },
  async deleteAgendaConfiguration(id: number): Promise<{message: string}> {
    const res = await this.fetch(`/agenda_configurations/${id}`, {method: "delete"});
    return res;
  }
  
} as AgendaConfigurationMixin;
